import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { apiRequest } from "../../../services/apiService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SurveyListHeader from "../../../component/common/layout/SurveyListHeader";
import SurveyListSidebar from "../../../component/common/layout/SurveyListSidebar";
import AccountSettingSidebar from "../../../component/common/layout/AccountSettingSidebar";
import NestedMenuToggle from "./NestedMenuToggle";

const TicketNotificationSettings = () => {
  const { t } = useTranslation();
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, recipientsRes] = await Promise.all([
        apiRequest({ method: "POST", url: "/users/getUserList", data: { startingLimit: 0, resultsPerPage: 500, search: "" } }),
        apiRequest({ method: "POST", url: "/ticket-notifications/get" }),
      ]);

      if (usersRes.response?.data?.data) {
        setAllUsers(usersRes.response.data.data);
      }
      if (recipientsRes.response?.data?.data) {
        setSelectedUsers(recipientsRes.response.data.data.map(r => ({
          user_id: r.user_id,
          name: r.name,
          email: r.email,
        })));
      }
    } catch (e) {
      console.error("Error loading ticket notification settings:", e);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { response, error } = await apiRequest({
        method: "POST",
        url: "/ticket-notifications/update",
        data: { user_ids: selectedUsers.map(u => u.user_id) },
        showErrorToast: false,
      });
      if (response?.data?.statusCode === 200) {
        toast.success(t('settings.ticket_notification_settings.success'));
      } else {
        toast.error(error?.response?.data?.msg || t('settings.ticket_notification_settings.error'));
      }
    } catch (e) {
      toast.error(t('settings.ticket_notification_settings.error'));
    }
    setSaving(false);
  };

  const addUser = (user) => {
    if (!selectedUsers.find(u => u.user_id === (user.id || user.user_id))) {
      setSelectedUsers([...selectedUsers, {
        user_id: user.id || user.user_id,
        name: user.name,
        email: user.email,
      }]);
    }
    setSearchTerm("");
    setDropdownOpen(false);
  };

  const removeUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.user_id !== userId));
  };

  const selectedIds = new Set(selectedUsers.map(u => u.user_id));
  const filteredUsers = allUsers.filter(u =>
    !selectedIds.has(u.id) &&
    (u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     u.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <>
      <div className="d-flex flex-column main-outer-wrapper main-site-wrapper bg-white">
        <SurveyListHeader />
        <div id="viewport" className="d-flex bg-white">
          <SurveyListSidebar />
          <div className="flex-grow-1 d-flex">
            <AccountSettingSidebar />
            <div className="flex-grow-1 page-content-wrap">
              <div className="page-heading">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/admin/settings/profile">{t('settings.title')}</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/admin/settings/profile">{t('settings.general_breadcrumb')}</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      <strong>{t('settings.ticket_notifications')}</strong>
                    </li>
                  </ol>
                </nav>
                <div className="d-flex align-items-center">
                  <NestedMenuToggle />
                  <div className="flex-grow-1 ps-3 ps-lg-0">
                    <h1>{t('settings.ticket_notification_settings.title')}</h1>
                  </div>
                </div>
              </div>

              <div className="profile-wrapper">
                <p style={{ color: "#666", fontSize: "14px", margin: "0 0 24px 0" }}>
                  {t('settings.ticket_notification_settings.description')}
                </p>

                {loading ? (
                  <div className="text-center py-5">
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                    <span className="ms-2">{t('settings.loading_settings')}</span>
                  </div>
                ) : (
                  <>
                    <div style={{
                      border: "2px solid #e60000",
                      borderRadius: "12px",
                      padding: "24px",
                      background: "#fff5f5",
                      marginBottom: "20px",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                        <i className="fa fa-envelope" style={{ color: "#e60000", fontSize: "18px" }}></i>
                        <span style={{ fontWeight: "600", fontSize: "16px", color: "#333" }}>
                          {t('settings.ticket_notification_settings.recipients_title')}
                        </span>
                      </div>
                      <p style={{ color: "#666", fontSize: "13px", margin: "0 0 16px 0", lineHeight: "1.6" }}>
                        {t('settings.ticket_notification_settings.recipients_description')}
                      </p>

                      <div ref={wrapperRef} style={{ position: "relative", marginBottom: "16px", maxWidth: "500px" }}>
                        <label style={{ fontSize: "12px", fontWeight: "600", color: "#555", display: "block", marginBottom: "6px" }}>
                          {t('settings.ticket_notification_settings.add_users')}
                        </label>
                        <input
                          type="text"
                          placeholder={t('settings.ticket_notification_settings.search_placeholder')}
                          value={searchTerm}
                          onChange={(e) => { setSearchTerm(e.target.value); setDropdownOpen(true); }}
                          onFocus={() => setDropdownOpen(true)}
                          style={{
                            width: "100%",
                            padding: "10px 14px",
                            borderRadius: "8px",
                            border: "1px solid #ddd",
                            fontSize: "14px",
                            outline: "none",
                          }}
                        />
                        {dropdownOpen && filteredUsers.length > 0 && (
                          <div style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            maxHeight: "200px",
                            overflowY: "auto",
                            background: "#fff",
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            zIndex: 100,
                          }}>
                            {filteredUsers.map(user => (
                              <div
                                key={user.id}
                                onClick={() => addUser(user)}
                                style={{
                                  padding: "10px 14px",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  borderBottom: "1px solid #f0f0f0",
                                  transition: "background 0.15s",
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = "#f7f7f7"}
                                onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                              >
                                <div style={{ fontWeight: "500", color: "#333" }}>{user.name}</div>
                                <div style={{ fontSize: "12px", color: "#888" }}>{user.email}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {selectedUsers.length === 0 ? (
                        <div style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "#999",
                          fontSize: "14px",
                          background: "#fafafa",
                          borderRadius: "8px",
                          border: "1px dashed #ddd",
                        }}>
                          {t('settings.ticket_notification_settings.no_users')}
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                          {selectedUsers.map(user => (
                            <div
                              key={user.user_id}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 12px",
                                background: "#fff",
                                border: "1px solid #e0e0e0",
                                borderRadius: "20px",
                                fontSize: "13px",
                              }}
                            >
                              <div style={{
                                width: "28px",
                                height: "28px",
                                borderRadius: "50%",
                                background: "#e60000",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                                fontWeight: "600",
                                flexShrink: 0,
                              }}>
                                {(user.name || "?").charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontWeight: "500", color: "#333", lineHeight: "1.2" }}>{user.name}</div>
                                <div style={{ fontSize: "11px", color: "#888" }}>{user.email}</div>
                              </div>
                              <button
                                onClick={() => removeUser(user.user_id)}
                                style={{
                                  border: "none",
                                  background: "none",
                                  cursor: "pointer",
                                  color: "#999",
                                  fontSize: "16px",
                                  padding: "0 4px",
                                  lineHeight: 1,
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = "#e60000"}
                                onMouseLeave={(e) => e.currentTarget.style.color = "#999"}
                                title={t('settings.ticket_notification_settings.remove')}
                              >
                                &times;
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {window.modulePermissions?.M10?.edit == 1 && (
                    <div className="btn-wrap">
                      <button
                        className="formsbutton btn-bordered btn"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <span className="white-spinner spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span className="visually-hidden">{t('settings.loading')}</span>
                          </>
                        ) : (
                          t('settings.save_changes')
                        )}
                      </button>
                    </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketNotificationSettings;
